import { TaxonomicFilterGroupType, TaxonomicFilterValue } from 'lib/components/TaxonomicFilter/types'
import { PERCENT_STACK_VIEW_DISPLAY_TYPE } from 'lib/constants'
import { dayjs } from 'lib/dayjs'
import { getAppContext } from 'lib/utils/getAppContext'

import { ProductAnalyticsInsightNodeKind } from '~/queries/nodes/InsightQuery/defaults'
import {
    AccountsQuery,
    AccountsTableQuery,
    ActionsNode,
    ActorsQuery,
    AnyDataWarehouseNode,
    AnyEntityNode,
    BreakdownFilter,
    CompareFilter,
    DataTableNode,
    DataVisualizationNode,
    DataWarehouseNode,
    DataWarehouseSourceUsage,
    DatabaseSchemaQuery,
    DateRange,
    EndpointsUsageOverviewQuery,
    EndpointsUsageTableQuery,
    EndpointsUsageTrendsQuery,
    ErrorTrackingIssueCorrelationQuery,
    ErrorTrackingQuery,
    EventsNode,
    EventsQuery,
    ExperimentFunnelsQuery,
    ExperimentMetric,
    ExperimentTrendsQuery,
    FunnelsDataWarehouseNode,
    FunnelsQuery,
    GoalLine,
    GroupNode,
    GroupsQuery,
    HogQLMetadata,
    HogQLQuery,
    HogQuery,
    InsightActorsQuery,
    InsightFilter,
    InsightFilterProperty,
    InsightQueryNode,
    InsightVizNode,
    LifecycleDataWarehouseNode,
    LifecycleQuery,
    MarketingAnalyticsAggregatedQuery,
    MarketingAnalyticsTableQuery,
    MathType,
    MetricsQuery,
    Node,
    NodeKind,
    NonIntegratedConversionsTableQuery,
    PathsQuery,
    PathsV2Query,
    PersonsNode,
    ProductAnalyticsInsightQueryNode,
    QuerySchema,
    QueryStatusResponse,
    ResultCustomizationBy,
    ResultCustomizationByPosition,
    ResultCustomizationByValue,
    RetentionQuery,
    SavedInsightNode,
    SessionAttributionExplorerQuery,
    SessionQuery,
    SessionsQuery,
    StickinessQuery,
    TracesQuery,
    TrendsFormulaNode,
    TrendsQuery,
    WebGoalsQuery,
    WebNotableChangesQuery,
    WebOverviewQuery,
    WebStatsTableQuery,
    WebVitalsPathBreakdownQuery,
    WebVitalsQuery,
} from '~/queries/schema/schema-general'
import { BaseMathType, ChartDisplayType, FunnelVizType, GroupTypeIndex, IntervalType } from '~/types'

import { LATEST_VERSIONS } from './latest-versions'

export function isDataNode(node?: Record<string, any> | null): node is EventsQuery | PersonsNode {
    return (
        isEventsNode(node) ||
        isActionsNode(node) ||
        isPersonsNode(node) ||
        isEventsQuery(node) ||
        isActorsQuery(node) ||
        isHogQLQuery(node) ||
        isHogQLMetadata(node)
    )
}

export function isNodeWithSource(node?: Record<string, any> | null): node is DataTableNode | InsightVizNode {
    if (!node) {
        return false
    }

    return isDataTableNode(node) || isDataVisualizationNode(node) || isInsightVizNode(node)
}

export function isEventsNode(node?: Record<string, any> | null): node is EventsNode {
    return node?.kind === NodeKind.EventsNode
}

export function isGroupNode(node?: Record<string, any> | null): node is GroupNode {
    return node?.kind === NodeKind.GroupNode
}

export function isEventsQuery(node?: Record<string, any> | null): node is EventsQuery {
    return node?.kind === NodeKind.EventsQuery
}

export function isSessionsQuery(node?: Record<string, any> | null): node is SessionsQuery {
    return node?.kind === NodeKind.SessionsQuery
}

export function isActionsNode(node?: Record<string, any> | null): node is ActionsNode {
    return node?.kind === NodeKind.ActionsNode
}

export function isDataWarehouseNode(node?: Record<string, any> | null): node is DataWarehouseNode {
    return node?.kind === NodeKind.DataWarehouseNode
}

export function isFunnelsDataWarehouseNode(node?: Record<string, any> | null): node is FunnelsDataWarehouseNode {
    return node?.kind === NodeKind.FunnelsDataWarehouseNode
}

export function isLifecycleDataWarehouseNode(node?: Record<string, any> | null): node is LifecycleDataWarehouseNode {
    return node?.kind === NodeKind.LifecycleDataWarehouseNode
}

export function isAnyDataWarehouseNode(node?: Record<string, any> | null): node is AnyDataWarehouseNode {
    return isDataWarehouseNode(node) || isFunnelsDataWarehouseNode(node) || isLifecycleDataWarehouseNode(node)
}

/** @deprecated `ActorsQuery` is now used instead of `PersonsNode`. */
export function isPersonsNode(node?: Record<string, any> | null): node is PersonsNode {
    return node?.kind === NodeKind.PersonsNode
}

export function isActorsQuery(node?: Record<string, any> | null): node is ActorsQuery {
    return node?.kind === NodeKind.ActorsQuery
}

export function isInsightActorsQuery(node?: Record<string, any> | null): node is InsightActorsQuery {
    return node?.kind === NodeKind.InsightActorsQuery
}

export function isDataTableNode(node?: Record<string, any> | null): node is DataTableNode {
    return node?.kind === NodeKind.DataTableNode
}

/** Previously SQL queries by default were `DataTableNode`s. However now new SQL queries are `DataVisualizationNode`s */
export function isDataTableNodeWithHogQLQuery(node?: Record<string, any> | null): node is DataTableNode & {
    source: HogQLQuery
} {
    return isDataTableNode(node) && isHogQLQuery(node.source)
}

export function isDataVisualizationNode(node?: Record<string, any> | null): node is DataVisualizationNode {
    return node?.kind === NodeKind.DataVisualizationNode
}

export function convertDataTableNodeToDataVisualizationNode(node: Node | null): Node | null {
    if (!isDataTableNodeWithHogQLQuery(node)) {
        return node
    }

    const {
        kind: _kind,
        source,
        columns,
        hiddenColumns: legacyHiddenColumns,
        pinnedColumns: legacyPinnedColumns,
        ...rest
    } = node
    const hiddenColumns = new Set(legacyHiddenColumns ?? [])
    const visibleColumns = columns?.filter((column) => !hiddenColumns.has(column))
    const tableSettingsColumns = visibleColumns?.length ? visibleColumns.map((column) => ({ column })) : undefined
    const pinnedColumns = legacyPinnedColumns?.filter((column) => !hiddenColumns.has(column))
    const mappedTableSettings =
        tableSettingsColumns || pinnedColumns?.length
            ? {
                  ...(tableSettingsColumns ? { columns: tableSettingsColumns } : {}),
                  ...(pinnedColumns?.length ? { pinnedColumns } : {}),
              }
            : undefined
    const tableSettings = {
        ...(rest as Partial<DataVisualizationNode>).tableSettings,
        ...mappedTableSettings,
    }

    return {
        ...rest,
        kind: NodeKind.DataVisualizationNode,
        source: source as HogQLQuery,
        display: ChartDisplayType.ActionsTable,
        ...(Object.keys(tableSettings).length ? { tableSettings } : {}),
    } as DataVisualizationNode
}

export function isSavedInsightNode(node?: Record<string, any> | null): node is SavedInsightNode {
    return node?.kind === NodeKind.SavedInsightNode
}

export function isInsightVizNode(node?: Record<string, any> | null): node is InsightVizNode {
    return node?.kind === NodeKind.InsightVizNode
}

export function isHogQuery(node?: Record<string, any> | null): node is HogQuery {
    return node?.kind === NodeKind.HogQuery
}

export function isHogQLQuery(node?: Record<string, any> | null): node is HogQLQuery {
    return node?.kind === NodeKind.HogQLQuery
}

export function isHogQLMetadata(node?: Record<string, any> | null): node is HogQLMetadata {
    return node?.kind === NodeKind.HogQLMetadata
}

export function isMetricsQuery(node?: Record<string, any> | null): node is MetricsQuery {
    return node?.kind === NodeKind.MetricsQuery
}

export function isEndpointsUsageOverviewQuery(node?: Record<string, any> | null): node is EndpointsUsageOverviewQuery {
    return node?.kind === NodeKind.EndpointsUsageOverviewQuery
}

export function isEndpointsUsageTableQuery(node?: Record<string, any> | null): node is EndpointsUsageTableQuery {
    return node?.kind === NodeKind.EndpointsUsageTableQuery
}

export function isEndpointsUsageTrendsQuery(node?: Record<string, any> | null): node is EndpointsUsageTrendsQuery {
    return node?.kind === NodeKind.EndpointsUsageTrendsQuery
}

export function isEndpointsUsageQuery(
    node?: Record<string, any> | null
): node is EndpointsUsageOverviewQuery | EndpointsUsageTableQuery | EndpointsUsageTrendsQuery {
    return isEndpointsUsageOverviewQuery(node) || isEndpointsUsageTableQuery(node) || isEndpointsUsageTrendsQuery(node)
}

export function isWebOverviewQuery(node?: Record<string, any> | null): node is WebOverviewQuery {
    return node?.kind === NodeKind.WebOverviewQuery
}

export function isWebStatsTableQuery(node?: Record<string, any> | null): node is WebStatsTableQuery {
    return node?.kind === NodeKind.WebStatsTableQuery
}

export function isWebAnalyticsInsightQuery(
    node?: Record<string, any> | null
): node is WebStatsTableQuery | WebOverviewQuery {
    return isWebStatsTableQuery(node) || isWebOverviewQuery(node)
}

export function isWebExternalClicksQuery(node?: Record<string, any> | null): boolean {
    return node?.kind === NodeKind.WebExternalClicksTableQuery
}

export function isWebBotsTableQuery(node?: Record<string, any> | null): boolean {
    return node?.kind === NodeKind.WebBotsTableQuery
}

export function isWebGoalsQuery(node?: Record<string, any> | null): node is WebGoalsQuery {
    return node?.kind === NodeKind.WebGoalsQuery
}

export function isWebNotableChangesQuery(node?: Record<string, any> | null): node is WebNotableChangesQuery {
    return node?.kind === NodeKind.WebNotableChangesQuery
}

export function isMarketingAnalyticsTableQuery(
    node?: Record<string, any> | null
): node is MarketingAnalyticsTableQuery {
    return node?.kind === NodeKind.MarketingAnalyticsTableQuery
}

export function isMarketingAnalyticsAggregatedQuery(
    node?: Record<string, any> | null
): node is MarketingAnalyticsAggregatedQuery {
    return node?.kind === NodeKind.MarketingAnalyticsAggregatedQuery
}

export function isNonIntegratedConversionsTableQuery(
    node?: Record<string, any> | null
): node is NonIntegratedConversionsTableQuery {
    return node?.kind === NodeKind.NonIntegratedConversionsTableQuery
}

export function isTracesQuery(node?: Record<string, any> | null): node is TracesQuery {
    return node?.kind === NodeKind.TracesQuery
}

export function isSessionQuery(node?: Record<string, any> | null): node is SessionQuery {
    return node?.kind === NodeKind.SessionQuery
}

export function isWebVitalsQuery(node?: Record<string, any> | null): node is WebVitalsQuery {
    return node?.kind === NodeKind.WebVitalsQuery
}

export function isWebVitalsPathBreakdownQuery(node?: Record<string, any> | null): node is WebVitalsPathBreakdownQuery {
    return node?.kind === NodeKind.WebVitalsPathBreakdownQuery
}

export function isSessionAttributionExplorerQuery(
    node?: Record<string, any> | null
): node is SessionAttributionExplorerQuery {
    return node?.kind === NodeKind.SessionAttributionExplorerQuery
}

export function isErrorTrackingQuery(node?: Record<string, any> | null): node is ErrorTrackingQuery {
    return node?.kind === NodeKind.ErrorTrackingQuery
}

export function isExperimentMetric(
    metric: ExperimentMetric | ExperimentFunnelsQuery | ExperimentTrendsQuery
): metric is ExperimentMetric {
    return metric.kind === NodeKind.ExperimentMetric
}

export function isErrorTrackingIssueCorrelationQuery(
    node?: Record<string, any> | null
): node is ErrorTrackingIssueCorrelationQuery {
    return node?.kind === NodeKind.ErrorTrackingIssueCorrelationQuery
}

export function containsHogQLQuery(node?: Record<string, any> | null): boolean {
    if (!node) {
        return false
    }
    return isHogQLQuery(node) || (isNodeWithSource(node) && isHogQLQuery(node.source))
}

/*
 * Insight Queries
 */

export function isTrendsQuery(node?: Record<string, any> | null): node is TrendsQuery {
    return node?.kind === NodeKind.TrendsQuery
}

export function isFunnelsQuery(node?: Record<string, any> | null): node is FunnelsQuery {
    return node?.kind === NodeKind.FunnelsQuery
}

export function isRetentionQuery(node?: Record<string, any> | null): node is RetentionQuery {
    return node?.kind === NodeKind.RetentionQuery
}

export function isPathsQuery(node?: Record<string, any> | null): node is PathsQuery {
    return node?.kind === NodeKind.PathsQuery
}

export function isPathsV2Query(node?: Record<string, any> | null): node is PathsV2Query {
    return node?.kind === NodeKind.PathsV2Query
}

export function isStickinessQuery(node?: Record<string, any> | null): node is StickinessQuery {
    return node?.kind === NodeKind.StickinessQuery
}

export function isLifecycleQuery(node?: Record<string, any> | null): node is LifecycleQuery {
    return node?.kind === NodeKind.LifecycleQuery
}

export function isInsightQueryWithDisplay(
    node?: Record<string, any> | null
): node is TrendsQuery | RetentionQuery | StickinessQuery {
    return isTrendsQuery(node) || isStickinessQuery(node) || isRetentionQuery(node)
}

export function isInsightQueryWithBreakdown(
    node?: Record<string, any> | null
): node is TrendsQuery | FunnelsQuery | RetentionQuery {
    return isTrendsQuery(node) || isFunnelsQuery(node) || isRetentionQuery(node)
}

export function isInsightQueryWithCompare(
    node?: Record<string, any> | null
): node is TrendsQuery | StickinessQuery | WebStatsTableQuery | WebOverviewQuery | FunnelsQuery {
    return (
        isTrendsQuery(node) ||
        isStickinessQuery(node) ||
        isWebStatsTableQuery(node) ||
        isWebOverviewQuery(node) ||
        isFunnelsQuery(node)
    )
}

export function isDatabaseSchemaQuery(node?: Node): node is DatabaseSchemaQuery {
    return node?.kind === NodeKind.DatabaseSchemaQuery
}

export function isQueryForGroup(query: PersonsNode | ActorsQuery): boolean {
    return (
        isActorsQuery(query) &&
        isInsightActorsQuery(query.source) &&
        isRetentionQuery(query.source.source) &&
        query.source.source.aggregation_group_type_index != null
    )
}

export function isAsyncResponse(response: NonNullable<QuerySchema['response']>): response is QueryStatusResponse {
    return 'query_status' in response && response.query_status
}

export function shouldQueryBeAsync(query: Node): boolean {
    return (
        isInsightQueryNode(query) ||
        isHogQLQuery(query) ||
        isTracesQuery(query) ||
        isSessionQuery(query) ||
        (isDataTableNode(query) &&
            (isInsightQueryNode(query.source) || isTracesQuery(query.source) || isSessionQuery(query.source))) ||
        (isDataVisualizationNode(query) &&
            (isInsightQueryNode(query.source) || isTracesQuery(query.source) || isSessionQuery(query.source)))
    )
}

export function isInsightQueryWithSeries(
    node?: Node
): node is TrendsQuery | FunnelsQuery | StickinessQuery | LifecycleQuery {
    return isTrendsQuery(node) || isFunnelsQuery(node) || isStickinessQuery(node) || isLifecycleQuery(node)
}

export function isInsightQueryNode(node?: Record<string, any> | null): node is InsightQueryNode {
    return (
        isTrendsQuery(node) ||
        isFunnelsQuery(node) ||
        isRetentionQuery(node) ||
        isPathsQuery(node) ||
        isPathsV2Query(node) ||
        isStickinessQuery(node) ||
        isLifecycleQuery(node) ||
        isWebStatsTableQuery(node) ||
        isWebOverviewQuery(node)
    )
}

export function dateRangeFor(node?: Node): DateRange | undefined {
    if (isInsightVizNode(node)) {
        return node.source.dateRange
    } else if (isInsightQueryNode(node)) {
        return node.dateRange
    } else if (isActionsNode(node)) {
        return undefined
    } else if (isEventsNode(node)) {
        return undefined
    } else if (isPersonsNode(node)) {
        return undefined
    } else if (isDataTableNode(node)) {
        return undefined
    }

    return undefined
}

export const getInterval = (query: InsightQueryNode): IntervalType | undefined => {
    if (isInsightQueryWithSeries(query)) {
        return query.interval
    }
    if (isWebStatsTableQuery(query) || isWebOverviewQuery(query)) {
        return query.interval ?? 'day'
    }
    return undefined
}

// For trends/stickiness, ActionsStackedBar is a deprecated alias of ActionsBar (which renders stacked):
// the UI never emits it, but the API and MCP accept it. Normalizing here — the point all `display`
// selectors derive from — makes such insights behave exactly like their UI-created equivalents.
const normalizeDisplay = (display: ChartDisplayType | undefined): ChartDisplayType | undefined =>
    display === ChartDisplayType.ActionsStackedBar ? ChartDisplayType.ActionsBar : display

export const getDisplay = (query: InsightQueryNode): ChartDisplayType | undefined => {
    if (isStickinessQuery(query)) {
        return normalizeDisplay(query.stickinessFilter?.display)
    } else if (isTrendsQuery(query)) {
        return normalizeDisplay(query.trendsFilter?.display)
    }
    return undefined
}

// Display types whose viz paints to a <canvas> (Chart.js / quill-charts), which repaints on every resize
// frame. Everything else renders as DOM/SVG and is cheap to keep mounted while a tile is resized.
const CANVAS_CHART_DISPLAY_TYPES = new Set<ChartDisplayType>([
    ChartDisplayType.Auto,
    ChartDisplayType.ActionsLineGraph,
    ChartDisplayType.ActionsLineGraphCumulative,
    ChartDisplayType.ActionsAreaGraph,
    ChartDisplayType.ActionsBar,
    ChartDisplayType.ActionsUnstackedBar,
    ChartDisplayType.ActionsStackedBar,
    ChartDisplayType.ActionsBarValue,
    ChartDisplayType.ActionsPie,
    ChartDisplayType.Metric,
    ChartDisplayType.BoxPlot,
    ChartDisplayType.SlopeGraph,
    ChartDisplayType.TwoDimensionalHeatmap,
])

type QueryVizCanvasClassification = 'canvas' | 'non-canvas' | 'unknown'

function classifyQueryVizCanvas(query?: Node | null): QueryVizCanvasClassification {
    if (isDataTableNode(query)) {
        return 'non-canvas'
    }
    if (isDataVisualizationNode(query)) {
        if (!query.display) {
            return 'non-canvas'
        }
        if (query.display === ChartDisplayType.Auto) {
            return 'unknown'
        }
        return CANVAS_CHART_DISPLAY_TYPES.has(query.display) ? 'canvas' : 'non-canvas'
    }
    if (isInsightVizNode(query)) {
        const source = query.source
        if (isRetentionQuery(source) || isPathsQuery(source)) {
            return 'non-canvas'
        }
        if (isFunnelsQuery(source)) {
            // Steps (default) and Trends paint to canvas; Flow (Sankey) is SVG and TimeToConvert is a DOM table.
            const vizType = source.funnelsFilter?.funnelVizType
            return vizType !== FunnelVizType.Flow && vizType !== FunnelVizType.TimeToConvert ? 'canvas' : 'non-canvas'
        }
        return CANVAS_CHART_DISPLAY_TYPES.has(getDisplay(source) ?? ChartDisplayType.Auto) ? 'canvas' : 'non-canvas'
    }
    return 'unknown'
}

/**
 * Whether an insight's viz may paint to a <canvas>. Unknown visualizations count as canvas so resize throttling
 * remains conservative.
 */
export function queryVizRendersToCanvas(query?: Node | null): boolean {
    return classifyQueryVizCanvas(query) !== 'non-canvas'
}

/** Whether an insight's viz is definitely canvas-backed and safe to unmount when the page is hidden. */
export function queryVizDefinitelyRendersToCanvas(query?: Node | null): boolean {
    return classifyQueryVizCanvas(query) === 'canvas'
}

export const getFormula = (query: InsightQueryNode | null): string | undefined => {
    if (isTrendsQuery(query)) {
        return (
            query.trendsFilter?.formulaNodes?.[0]?.formula ||
            query.trendsFilter?.formulas?.[0] ||
            query.trendsFilter?.formula
        )
    }
    return undefined
}

export const getFormulas = (query: InsightQueryNode | null): string[] | undefined => {
    if (isTrendsQuery(query)) {
        return (
            query.trendsFilter?.formulaNodes?.map((node) => node.formula) ||
            query.trendsFilter?.formulas ||
            (query.trendsFilter?.formula ? [query.trendsFilter.formula] : undefined)
        )
    }
    return undefined
}

export const getFormulaNodes = (query: InsightQueryNode | null): TrendsFormulaNode[] | undefined => {
    if (isTrendsQuery(query)) {
        return (
            query.trendsFilter?.formulaNodes ||
            query.trendsFilter?.formulas?.map((formula) => ({ formula })) ||
            (query.trendsFilter?.formula ? [{ formula: query.trendsFilter.formula }] : undefined)
        )
    }
    return undefined
}

export const getSeries = (query: InsightQueryNode): (AnyEntityNode<AnyDataWarehouseNode> | GroupNode)[] | undefined => {
    if (isInsightQueryWithSeries(query)) {
        return query.series
    }
    return undefined
}

/** Client-side check: does this query have a data-warehouse-backed series (insight surface)?
 * For raw SQL / HogQL queries the client can't know without the backend — use
 * `dataWarehouseSourcesFromResponse` on the query response instead. */
export function queryUsesDataWarehouse(query?: Record<string, any> | null): boolean {
    if (!query) {
        return false
    }
    const source = isInsightVizNode(query) ? query.source : query
    if (isInsightQueryNode(source)) {
        return !!getSeries(source)?.some((entity) => isAnyDataWarehouseNode(entity))
    }
    return false
}

/** Extract the connector-synced warehouse sources a query touched from its response, if the
 * backend populated them (currently HogQL / SQL-editor responses). Always returns an array. */
export function dataWarehouseSourcesFromResponse(response: unknown): DataWarehouseSourceUsage[] {
    if (response && typeof response === 'object' && Array.isArray((response as any).used_data_warehouse_sources)) {
        return (response as any).used_data_warehouse_sources
    }
    return []
}

export const getBreakdown = (query: InsightQueryNode): BreakdownFilter | undefined => {
    if (isInsightQueryWithBreakdown(query)) {
        return query.breakdownFilter
    }
    return undefined
}

export const getCompareFilter = (query: InsightQueryNode): CompareFilter | undefined => {
    if (isInsightQueryWithCompare(query)) {
        return query.compareFilter
    }
    return undefined
}

export const getAggregationGroupTypeIndex = (query: InsightQueryNode): GroupTypeIndex | null | undefined => {
    if (!isStickinessQuery(query) && 'aggregation_group_type_index' in query) {
        return query.aggregation_group_type_index as GroupTypeIndex | null | undefined
    }
    return undefined
}

export const getShowLegend = (query: InsightQueryNode): boolean | undefined => {
    if (isStickinessQuery(query)) {
        return query.stickinessFilter?.showLegend
    } else if (isTrendsQuery(query)) {
        return query.trendsFilter?.showLegend
    } else if (isLifecycleQuery(query)) {
        return query.lifecycleFilter?.showLegend
    } else if (isFunnelsQuery(query)) {
        return query.funnelsFilter?.showLegend
    }
    return undefined
}

// Widened to `string` (not the literal union) to match getYAxisScaleType — kea-typegen can't
// serialize an inline string-literal union and emits a broken type; consumers narrow as needed.
export const getLegendPosition = (query: InsightQueryNode): string | undefined => {
    if (isTrendsQuery(query)) {
        return query.trendsFilter?.legendPosition
    } else if (isStickinessQuery(query)) {
        return query.stickinessFilter?.legendPosition
    } else if (isLifecycleQuery(query)) {
        return query.lifecycleFilter?.legendPosition
    } else if (isFunnelsQuery(query)) {
        return query.funnelsFilter?.legendPosition
    }
    return undefined
}

export const getShowAlertThresholdLines = (query: InsightQueryNode): boolean | undefined => {
    if (isTrendsQuery(query)) {
        return query.trendsFilter?.showAlertThresholdLines
    }
    return undefined
}

export const getShowAnnotations = (query: InsightQueryNode): boolean | undefined => {
    if (isTrendsQuery(query)) {
        return query.trendsFilter?.showAnnotations
    } else if (isFunnelsQuery(query)) {
        return query.funnelsFilter?.showAnnotations
    }
    return undefined
}

export const getShowLabelsOnSeries = (query: InsightQueryNode): boolean | undefined => {
    if (isTrendsQuery(query)) {
        return query.trendsFilter?.showLabelsOnSeries
    }
    return undefined
}

export const getShowValuesOnSeries = (query: InsightQueryNode): boolean | undefined => {
    if (isLifecycleQuery(query)) {
        return query.lifecycleFilter?.showValuesOnSeries
    } else if (isStickinessQuery(query)) {
        return query.stickinessFilter?.showValuesOnSeries
    } else if (isTrendsQuery(query)) {
        return query.trendsFilter?.showValuesOnSeries
    } else if (isFunnelsQuery(query)) {
        return query.funnelsFilter?.showValuesOnSeries
    }
    return undefined
}

export const getShowPercentagesOnSeries = (query: InsightQueryNode): boolean | undefined => {
    if (isLifecycleQuery(query)) {
        return query.lifecycleFilter?.showPercentagesOnSeries
    }
    return undefined
}

export const getYAxisScaleType = (query: InsightQueryNode): string | undefined => {
    if (isTrendsQuery(query)) {
        return query.trendsFilter?.yAxisScaleType
    }
    return undefined
}

export const getShowMultipleYAxes = (query: InsightQueryNode): boolean | undefined => {
    if (isTrendsQuery(query)) {
        return query.trendsFilter?.showMultipleYAxes
    } else if (isStickinessQuery(query)) {
        return query.stickinessFilter?.showMultipleYAxes
    }
    return undefined
}

export const getResultCustomizationBy = (query: InsightQueryNode): ResultCustomizationBy | undefined => {
    if (isTrendsQuery(query)) {
        return query.trendsFilter?.resultCustomizationBy
    } else if (isStickinessQuery(query)) {
        return query.stickinessFilter?.resultCustomizationBy
    }
    return undefined
}

export function getResultCustomizations(query: FunnelsQuery): Record<string, ResultCustomizationByValue> | undefined
export function getResultCustomizations(
    query: TrendsQuery | StickinessQuery
): Record<number, ResultCustomizationByPosition> | undefined
export function getResultCustomizations(
    query: InsightQueryNode
): Record<string, ResultCustomizationByValue> | Record<number, ResultCustomizationByPosition> | undefined
export function getResultCustomizations(
    query: InsightQueryNode
): Record<string, ResultCustomizationByValue> | Record<number, ResultCustomizationByPosition> | undefined {
    if (isTrendsQuery(query)) {
        return query.trendsFilter?.resultCustomizations
    } else if (isStickinessQuery(query)) {
        return query.stickinessFilter?.resultCustomizations
    } else if (isFunnelsQuery(query)) {
        return query.funnelsFilter?.resultCustomizations
    }
    return undefined
}

export const getGoalLines = (query: InsightQueryNode): GoalLine[] | undefined => {
    if (isTrendsQuery(query)) {
        return query.trendsFilter?.goalLines
    } else if (isFunnelsQuery(query)) {
        return query.funnelsFilter?.goalLines
    } else if (isRetentionQuery(query)) {
        return query.retentionFilter?.goalLines
    }

    return undefined
}

export const supportsPercentStackView = (q: InsightQueryNode | null | undefined): boolean =>
    isTrendsQuery(q) && PERCENT_STACK_VIEW_DISPLAY_TYPE.includes(getDisplay(q) || ChartDisplayType.ActionsLineGraph)

export const getShowPercentStackView = (query: InsightQueryNode): boolean | undefined =>
    supportsPercentStackView(query) && (query as TrendsQuery)?.trendsFilter?.showPercentStackView

export const supportsBarValueStacking = (q: InsightQueryNode | null | undefined): boolean =>
    isTrendsQuery(q) && getDisplay(q) === ChartDisplayType.ActionsBarValue && hasBreakdownFilter(getBreakdown(q))

export const getStackBreakdownValues = (query: InsightQueryNode): boolean | undefined =>
    supportsBarValueStacking(query) && (query as TrendsQuery)?.trendsFilter?.stackBreakdownValues

export const nodeKindToFilterProperty: Record<ProductAnalyticsInsightNodeKind, InsightFilterProperty> = {
    [NodeKind.TrendsQuery]: 'trendsFilter',
    [NodeKind.FunnelsQuery]: 'funnelsFilter',
    [NodeKind.RetentionQuery]: 'retentionFilter',
    [NodeKind.PathsQuery]: 'pathsFilter',
    [NodeKind.PathsV2Query]: 'pathsV2Filter',
    [NodeKind.StickinessQuery]: 'stickinessFilter',
    [NodeKind.LifecycleQuery]: 'lifecycleFilter',
}

export function filterKeyForQuery(node: ProductAnalyticsInsightQueryNode): InsightFilterProperty {
    return nodeKindToFilterProperty[node.kind]
}

export function filterForQuery(node: ProductAnalyticsInsightQueryNode): InsightFilter | undefined {
    const filterProperty = nodeKindToFilterProperty[node.kind]
    return node[filterProperty as keyof InsightQueryNode] as InsightFilter | undefined
}

export function isQuoted(identifier: string): boolean {
    return (
        (identifier.startsWith('"') && identifier.endsWith('"')) ||
        (identifier.startsWith('`') && identifier.endsWith('`'))
    )
}

export function trimQuotes(identifier: string): string {
    if (isQuoted(identifier)) {
        return identifier.slice(1, -1)
    }
    return identifier
}

// Mirrors escape_chars_map in posthog/hogql/escape_sql.py: backslash and control chars must be escaped so a quoted identifier round-trips losslessly through the HogQL parser.
const HOGQL_IDENTIFIER_ESCAPE_MAP: Record<string, string> = {
    '\b': '\\b',
    '\f': '\\f',
    '\r': '\\r',
    '\n': '\\n',
    '\t': '\\t',
    '\0': '\\0',
    '\x07': '\\a',
    '\v': '\\v',
    '\\': '\\\\',
}

/** Make sure the property key is wrapped in quotes if it contains any special characters. */
export function escapePropertyAsHogQLIdentifier(identifier: string): string {
    if (identifier.match(/^[A-Za-z_$][A-Za-z0-9_$]*$/)) {
        // Same regex as in the backend escape_hogql_identifier
        return identifier // This identifier is simple
    }
    if (isQuoted(identifier)) {
        return identifier // This identifier is already quoted
    }
    return escapeRawPropertyAsHogQLIdentifier(identifier)
}

export function escapeRawPropertyAsHogQLIdentifier(identifier: string): string {
    if (identifier.match(/^[A-Za-z_$][A-Za-z0-9_$]*$/)) {
        return identifier
    }
    // Escape backslashes and control chars, then wrap; double an inner backtick (the parser rejects a backslash-escaped delimiter). The double-quote path needs no quote escaping since it is only taken when the identifier has no `"`.
    const escaped = Array.from(identifier, (c) => HOGQL_IDENTIFIER_ESCAPE_MAP[c] || c).join('')
    return !identifier.includes('"') ? `"${escaped}"` : `\`${escaped.replaceAll('`', '``')}\``
}

/** Quote each segment of a dotted HogQL reference independently. */
export function escapeDottedHogQLIdentifier(identifier: string): string {
    if (isQuoted(identifier) || !identifier.includes('.')) {
        return escapePropertyAsHogQLIdentifier(identifier)
    }

    return identifier
        .split('.')
        .map((segment) => escapePropertyAsHogQLIdentifier(segment))
        .join('.')
}

export function taxonomicEventFilterToHogQL(
    groupType: TaxonomicFilterGroupType,
    value: TaxonomicFilterValue
): string | null {
    if (groupType === TaxonomicFilterGroupType.EventProperties) {
        return `properties.${escapePropertyAsHogQLIdentifier(String(value))}`
    }
    if (groupType === TaxonomicFilterGroupType.PersonProperties) {
        return `person.properties.${escapePropertyAsHogQLIdentifier(String(value))}`
    }
    if (groupType === TaxonomicFilterGroupType.EventFeatureFlags) {
        return `properties.${escapePropertyAsHogQLIdentifier(String(value))}`
    }
    if (groupType === TaxonomicFilterGroupType.SessionProperties) {
        return `session.${escapePropertyAsHogQLIdentifier(String(value))}`
    }
    if (groupType === TaxonomicFilterGroupType.HogQLExpression && value) {
        return String(value)
    }
    return null
}

export function taxonomicPersonFilterToHogQL(
    groupType: TaxonomicFilterGroupType,
    value: TaxonomicFilterValue
): string | null {
    if (groupType === TaxonomicFilterGroupType.PersonProperties) {
        return `properties.${escapePropertyAsHogQLIdentifier(String(value))}`
    }
    if (groupType === TaxonomicFilterGroupType.HogQLExpression && value) {
        return String(value)
    }
    return null
}

export function taxonomicGroupFilterToHogQL(
    groupType: TaxonomicFilterGroupType,
    value: TaxonomicFilterValue
): string | null {
    if (groupType.startsWith(TaxonomicFilterGroupType.GroupsPrefix)) {
        return `properties.${escapePropertyAsHogQLIdentifier(String(value))}`
    }
    if (groupType === TaxonomicFilterGroupType.HogQLExpression && value) {
        return String(value)
    }
    return null
}

export function isHogQLAggregation(hogQl: string): boolean {
    return (
        hogQl.includes('count(') ||
        hogQl.includes('any(') ||
        hogQl.includes('sum(') ||
        hogQl.includes('avg(') ||
        hogQl.includes('min(') ||
        hogQl.includes('max(')
    )
}

declare const __hogqlBrand: unique symbol
export type HogQLQueryString = string & { readonly [__hogqlBrand]: void }

export interface HogQLIdentifier {
    __hogql_identifier: true
    identifier: string
}

function hogQLIdentifier(identifier: string): HogQLIdentifier {
    return {
        __hogql_identifier: true,
        identifier,
    }
}

function isHogQLIdentifier(value: any): value is HogQLIdentifier {
    return !!value?.__hogql_identifier
}

export interface HogQLRaw {
    __hogql_raw: true
    raw: string
}

function hogQLRaw(raw: string): HogQLRaw {
    return {
        __hogql_raw: true,
        raw,
    }
}

function isHogQLRaw(value: any): value is HogQLRaw {
    return !!value?.__hogql_raw
}

const ESCAPE_CHARS_MAP: Record<string, string> = {
    '\b': '\\b',
    '\f': '\\f',
    '\r': '\\r',
    '\n': '\\n',
    '\t': '\\t',
    '\0': '\\0',
    '\x07': '\\a',
    '\v': '\\v',
    '\\': '\\\\',
    "'": "\\'",
}

export function escapeHogQLString(value: string): string {
    let escaped = ''
    for (const char of value) {
        escaped += ESCAPE_CHARS_MAP[char] ?? char
    }
    return `'${escaped}'`
}

function formatHogQLValue(value: any): string {
    if (Array.isArray(value)) {
        return `[${value.map(formatHogQLValue).join(', ')}]`
    } else if (dayjs.isDayjs(value)) {
        const timezone = getAppContext()?.current_team?.timezone || 'UTC'
        return value.tz(timezone).format("'YYYY-MM-DD HH:mm:ss'")
    } else if (isHogQLIdentifier(value)) {
        return escapePropertyAsHogQLIdentifier(value.identifier)
    } else if (isHogQLRaw(value)) {
        return value.raw
    } else if (typeof value === 'string') {
        return escapeHogQLString(value)
    } else if (typeof value === 'number') {
        return String(value)
    } else if (value === null) {
        throw new Error(
            `null cannot be interpolated for SQL. if a null check is needed, make 'IS NULL' part of your query`
        )
    } else {
        throw new Error(`Unsupported interpolated value type: ${typeof value}`)
    }
}

/**
 * Template tag for HogQL formatting. Handles formatting of values for you.
 * @example hogql`SELECT * FROM events WHERE properties.text = ${text} AND timestamp > ${dayjs()}`
 */
export function hogql(strings: TemplateStringsArray, ...values: any[]): HogQLQueryString {
    return strings.reduce(
        (acc, str, i) => acc + str + (i < strings.length - 1 ? formatHogQLValue(values[i]) : ''),
        ''
    ) as unknown as HogQLQueryString
}
hogql.identifier = hogQLIdentifier
hogql.raw = hogQLRaw

type SingleBreakdownFilter = BreakdownFilter & {
    breakdown: NonNullable<BreakdownFilter['breakdown']>
}

type MultiBreakdownFilter = BreakdownFilter & {
    breakdowns: NonNullable<BreakdownFilter['breakdowns']>
}

type PopulatedBreakdownFilter = SingleBreakdownFilter | MultiBreakdownFilter

export function hasSingleBreakdown(breakdownFilter?: BreakdownFilter | null): breakdownFilter is SingleBreakdownFilter {
    return breakdownFilter?.breakdown != null
}

export function hasMultiBreakdown(breakdownFilter?: BreakdownFilter | null): breakdownFilter is MultiBreakdownFilter {
    return (breakdownFilter?.breakdowns?.length ?? 0) > 0
}

export function hasBreakdownFilter(
    breakdownFilter?: BreakdownFilter | null
): breakdownFilter is PopulatedBreakdownFilter {
    return hasSingleBreakdown(breakdownFilter) || hasMultiBreakdown(breakdownFilter)
}

export function isValidQueryForExperiment(query: Node): boolean {
    if (!isNodeWithSource(query) || !isFunnelsQuery(query.source)) {
        return false
    }

    const { series } = query.source
    return series.length >= 2 && !series?.some((node) => isDataWarehouseNode(node))
}

export function isGroupsQuery(node?: Record<string, any> | null): node is GroupsQuery {
    return node?.kind === NodeKind.GroupsQuery
}

export function isAccountsQuery(node?: Record<string, any> | null): node is AccountsQuery {
    return node?.kind === NodeKind.AccountsQuery
}

export function isAccountsTableQuery(node?: Record<string, any> | null): node is AccountsTableQuery {
    return node?.kind === NodeKind.AccountsTableQuery
}

export const TRAILING_MATH_TYPES = new Set<MathType>([BaseMathType.WeeklyActiveUsers, BaseMathType.MonthlyActiveUsers])

/**
 * Determines if a math type should display a warning based on the trends query interval and display category
 */
export function getMathTypeWarning(
    key: MathType,
    query: Record<string, any>,
    isTotalValue: boolean
): null | 'total' | 'monthly' | 'weekly' {
    let warning: null | 'total' | 'monthly' | 'weekly' = null

    if (isInsightVizNode(query) && isTrendsQuery(query.source) && TRAILING_MATH_TYPES.has(key)) {
        const trendsQuery = query.source
        const interval = trendsQuery?.interval || 'day'
        const isWeekOrLongerInterval = interval === 'week' || interval === 'month'
        const isMonthOrLongerInterval = interval === 'month'

        if (key === BaseMathType.MonthlyActiveUsers && isMonthOrLongerInterval) {
            warning = 'monthly'
        } else if (key === BaseMathType.WeeklyActiveUsers && isWeekOrLongerInterval) {
            warning = 'weekly'
        } else if (isTotalValue) {
            warning = 'total'
        }
    }

    return warning
}

/**
 * **Needs to be used on all hardcoded queries.**
 *
 * Recursively adds the latest version for the respective kind to each node. This
 * is necessary so that schema migrations don't run on hardcoded queries that
 * are already the latest version. */
export function setLatestVersionsOnQuery<T = any>(node: T, options?: { recursion?: boolean }): T {
    const recursion = options?.recursion ?? true

    if (node === null || typeof node !== 'object') {
        return node
    }

    if (recursion === true && Array.isArray(node)) {
        return (node as unknown as any[]).map((value) => setLatestVersionsOnQuery(value)) as unknown as T
    }

    const cloned: Record<string, any> = { ...(node as any) }

    if (
        'kind' in cloned &&
        Object.values(NodeKind).includes(cloned.kind) &&
        LATEST_VERSIONS[cloned.kind as NodeKind] > 1
    ) {
        const latest = LATEST_VERSIONS[cloned.kind as NodeKind]
        cloned.version = latest || 1
    }

    if (recursion === true) {
        for (const [key, value] of Object.entries(cloned)) {
            if (value !== null && typeof value === 'object') {
                cloned[key] = setLatestVersionsOnQuery(value)
            }
        }
    }

    return cloned as T
}

/** Checks whether a given query node satisfies all latest versions of the query schema. */
export function checkLatestVersionsOnQuery(node: any): boolean {
    if (node === null || typeof node !== 'object') {
        return true
    }

    if (Array.isArray(node)) {
        return node.every((value) => checkLatestVersionsOnQuery(value))
    }

    if ('kind' in node && Object.values(NodeKind).includes(node.kind)) {
        const latest = LATEST_VERSIONS[node.kind as NodeKind]
        if (node.version !== latest) {
            return false
        }
    }

    for (const value of Object.values(node)) {
        if (!checkLatestVersionsOnQuery(value)) {
            return false
        }
    }

    return true
}
